/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { INavLink } from '@fluentui/react/lib/Nav';
import * as React from 'react';

import { Constants } from '../../../common/Constants';
import { useSetSidebarDataMutation } from '../../../data/client/mutations/SetSidebarData.generated';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import { ApprovalUserRole } from '../../../data/types.generated';
import { isAdobeEnabled, isDocusignEnabled, isSyntexEnabled } from '../../../utilities/ECSConfigUtils';
import { getApprovalUserRoleFromSubEntityId } from '../../../utilities/SubEntityUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { useShowTemplateManagementEntry } from '../../../utilities/TemplateManagementUtils';
import { ApprovalsAppSidebarComponent, ApprovalsAppSidebarComponentProps } from './ApprovalsAppSidebar.component';
import { QueryData, SidebarItemKey, queryVariablesConfig } from './ListDataQueryVariables';

import { useSelectApprovalSource } from './SelectorUtils';

export const ApprovalsAppSidebarContainer: React.FC = (): JSX.Element => {
    const selectApprovalSource = useSelectApprovalSource();

    const host = getAppHost();
    const loc = host.getLocale();

    const showMsApprovals = host.getEcsConfig().MicrosoftTeamApprovals.MsApprovalsEnabled;

    const isAdminOrOwner = useShowTemplateManagementEntry();
    const showTemplateManagementButtonEveryone =
        host.getEcsConfig().MicrosoftTeamApprovals.TemplateManagementButtonEveryoneShow;

    const [setSidebarDataMutation] = useSetSidebarDataMutation();

    const {
        data: { listData },
    } = useListDataQuery();

    const basicQueryData = queryVariablesConfig.BasicSummary;
    const adobeSignQueryData = isAdobeEnabled() ? queryVariablesConfig.EsignSummary : null;
    const docusignQueryData = isDocusignEnabled() ? queryVariablesConfig.DocusignSummary : null;
    const MSApprovalsQueryData = showMsApprovals ? queryVariablesConfig.MsApprovalsSummary : null;
    const syntexQueryData = isSyntexEnabled() ? queryVariablesConfig.SyntexSummary : null;

    const approvalType = listData?.approvalType;
    const selectedKeyApprovalType = {
        Basic: SidebarItemKey.EasyApprovals,
        ESign: SidebarItemKey.EsignApprovals,
        Docusign: SidebarItemKey.DocusignApprovals,
        MsApprovals: SidebarItemKey.MsApprovals,
        Syntex: SidebarItemKey.SyntexApprovals,
    }[approvalType];

    const selectSidebarItem = React.useCallback((queryData: QueryData): void => {
        selectApprovalSource(queryData);
        // Reset list position on sidebar item switch
        window.dispatchEvent(new Event('resetApprovalsListPosition'));
        // eslint-disable-next-line
    }, []);

    // Add listener to dismiss floating sidebar on clicking event outside the sidebar within tablet screen width
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect((): (() => void) => {
        const onClick = (ev: MouseEvent): void => {
            const inTabletBoundary = window.innerWidth <= Constants.TABLET_UPPER_BOUNDARY;
            const targetNode = ev.target as Node;
            if (inTabletBoundary && targetNode && !ref.current.contains(targetNode)) {
                setSidebarDataMutation({
                    variables: {
                        isFloating: false,
                    },
                });
            }
        };
        window.addEventListener('click', onClick, true);
        return (): void => window.removeEventListener('click', onClick, true);
    }, [setSidebarDataMutation]);

    const selectItem1 = (): void => selectSidebarItem(basicQueryData);
    const selectItemAdobeSign = (): void => selectSidebarItem(adobeSignQueryData);
    const selectItemDocusign = (): void => selectSidebarItem(docusignQueryData);
    const selectItem3 = (): void => selectSidebarItem(MSApprovalsQueryData);
    const selectSyntex = (): void => selectSidebarItem(syntexQueryData);

    // Set sidebar item on first rendering, if a sub-entity id is passed in from Teams
    React.useEffect((): void => {
        const subEntityId = host.getSubEntityId();
        const userRole = getApprovalUserRoleFromSubEntityId(subEntityId);

        if (userRole) {
            if (userRole === ApprovalUserRole.Owner) {
                selectSidebarItem(queryVariablesConfig.BasicSent);
            } else {
                selectSidebarItem(queryVariablesConfig.BasicReceived);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const navLinks: Partial<INavLink[]> = [
        {
            key: basicQueryData.itemKey,
            name: loc.strings[basicQueryData.localizationNameKey],
            ariaLabel: loc.strings[basicQueryData.localizationNameKey],
            url: null,
            onClick: selectItem1,
            count: NaN,
        },
    ];

    if (adobeSignQueryData) {
        navLinks.push({
            key: adobeSignQueryData.itemKey,
            name: loc.strings[adobeSignQueryData.localizationNameKey],
            ariaLabel: loc.strings[adobeSignQueryData.localizationNameKey],
            url: null,
            onClick: selectItemAdobeSign,
            count: NaN,
        });
    }

    if (docusignQueryData) {
        navLinks.push({
            key: docusignQueryData.itemKey,
            name: loc.strings[docusignQueryData.localizationNameKey],
            ariaLabel: loc.strings[docusignQueryData.localizationNameKey],
            url: null,
            onClick: selectItemDocusign,
            count: NaN,
        });
    }

    if (MSApprovalsQueryData) {
        navLinks.push({
            key: MSApprovalsQueryData.itemKey,
            name: loc.strings[MSApprovalsQueryData.localizationNameKey],
            ariaLabel: loc.strings[MSApprovalsQueryData.localizationNameKey],
            url: null,
            onClick: selectItem3,
            count: NaN,
        });
    }

    if (syntexQueryData) {
        navLinks.push({
            key: syntexQueryData.itemKey,
            name: Constants.M365_ESIGN_NAME,
            ariaLabel: Constants.M365_ESIGN_NAME,
            url: null,
            onClick: selectSyntex,
            count: NaN,
        });
    }

    const selectedKey = selectedKeyApprovalType;

    const props: ApprovalsAppSidebarComponentProps = {
        selectedKey,
        navLinks,
        isAdminOrOwner,
        showTemplateManagementButtonEveryone,
    };

    return (
        <div ref={ref} className='approvals-app-sidebar-container'>
            <ApprovalsAppSidebarComponent {...props} />
        </div>
    );
};
